// ✅ src/pages/VehicleV2/BatteryCardMaintenanceListing.jsx  (UPDATED FULL FILE)
// Backend mount:
//   app.use("/api/batteryCardMaintenanceListing", batteryCardMaintenanceListing);
//
// APIs used:
//   GET  https://nissi-india.com/api/batteryCardMaintenanceListing/init?branchId=..&loginUserId=..&branchType=..
//   GET  https://nissi-india.com/api/batteryCardMaintenanceListing/list
//   POST https://nissi-india.com/api/batteryCardMaintenanceListing/delete
//   POST https://nissi-india.com/api/batteryCardMaintenanceListing/scrap
//
// Routing:
//   Add  : /vehicle/BatteryCardMaintenanceEntry
//   Edit : /vehicle/BatteryCardMaintenanceEntry?batteryCardMainId=123
//
// ✅ Updated pattern like WorkLocationMaster:
// - common localStorage/sessionStorage header pattern
// - inventory header support
// - useMemo + useCallback pattern
// - init based loading
// - pageLoading + loading states
// - same UI structure style
// - edit/add navigation kept
// - filter layout kept same
// - table/pagination/delete/scrap kept

import React, {
  useState,
  useContext,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import {
  FaPlus,
  FaSearch,
  FaFilter,
  FaTrash,
  FaChevronDown,
  FaChevronUp,
  FaBatteryFull,
  FaEdit,
} from "react-icons/fa";

const API_BASE = "https://nissi-india.com/api/batteryCardMaintenanceListing";

const toText = (v, def = "") => {
  if (v === null || v === undefined) return def;
  return String(v);
};

const toStr = (v, def = "") => {
  if (v === null || v === undefined) return def;
  return String(v).trim();
};

const toInt = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : def;
};

const defaultDropdowns = {
  batteryCodes: [{ value: "Select", label: "Select" }],
  batteryTypes: [{ value: "Select", label: "Select" }],
  stores: [{ value: "Select", label: "Select" }],
  vehicleTypes: [{ value: "Select", label: "Select" }],
  batteryStatus: [
    { value: "Process", label: "Process" },
    { value: "Scrapped", label: "Scrapped" },
  ],
};

const emptyFilters = {
  vehicleName: "",
  vehicleRegNo: "",
  fittedDate: "",
  storeId: "Select",
  batteryCodeId: "Select",
  batteryTypeId: "Select",
  vehicleTypeIndex: "Select",
  batteryStatus: "Process",
};

// Best-effort mapping from backend row -> UI row
const mapRow = (r) => {
  const id =
    r.fld_batterycardmainid ??
    r.batteryCardMainId ??
    r.id ??
    r.fld_id ??
    r.fld_batterycard_id;

  return {
    id: toInt(id),
    store: toText(r.fld_storename ?? r.storename ?? r.store ?? r.StoreName),
    vehicleMainType: toText(
      r.fld_vehicletypename ??
        r.fld_vehicle_subtypename ??
        r.vehicleMainType ??
        r.vehiclemaintype ??
        r.vehicleTypeName
    ),
    vehicleCode: toText(r.fld_vehicle_code ?? r.vehicleCode ?? r.vehiclecode),
    vehicleName: toText(r.fld_vehicle_name ?? r.vehicleName ?? r.vehiclename),
    vehicleRegistrationNumber: toText(
      r.fld_vehicle_regno ??
        r.vehicleRegNo ??
        r.vehicleRegistrationNumber ??
        r.vehicleregno
    ),
    batteryCode: toText(r.fld_batterycode ?? r.batteryCode ?? r.batterycode),
    batteryType: toText(r.fld_batterytype ?? r.batteryType ?? r.batterytype),
    fittedDate: toText(
      r.fld_Fitteddate ?? r.fld_fitteddate ?? r.fittedDate ?? r.fld_fitted_date
    ),
    currentRemoveDate: toText(
      r.currentdate ??
        r.currentRemoveDate ??
        r.fld_currentremovedate ??
        r.fld_remove_date ??
        r.fld_removedate
    ),
    actualLife: toText(r.fld_actuallife ?? r.actualLife ?? r.actuallife),
    currentLife: toText(r.fld_currentlife ?? r.currentLife ?? r.currentlife),
    alert: toText(r.alert ?? r.fld_alert ?? ""),
    batterystatus: toText(r.fld_batterystatus ?? r.batterystatus ?? ""),
  };
};

const BatteryCardMaintenanceListing = () => {
  const navigate = useNavigate();

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  // ✅ shared localStorage / sessionStorage pattern
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId =
    localStorage.getItem("branchId") ||
    localStorage.getItem("branchid") ||
    "8";
  const branch =
    localStorage.getItem("branchType") ||
    localStorage.getItem("branchtype") ||
    "HO";
  const userId =
    localStorage.getItem("userId") ||
    localStorage.getItem("loginuserid") ||
    "";
  const loginUserId = sessionStorage.getItem("loginUserId") || userId || "";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branch,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branch, userId]
  );

  // filters
  const [showFilters, setShowFilters] = useState(true);
  const [filters, setFilters] = useState(emptyFilters);

  // dropdown data
  const [dd, setDd] = useState(defaultDropdowns);

  // grid
  const [rows, setRows] = useState([]);

  // loading
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);

  // pagination
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);

  // selection
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  const totalPages = useMemo(() => {
    const t = Math.ceil(total / limit);
    return t > 0 ? t : 1;
  }, [total, limit]);

  const labelClass = `block text-sm font-medium mb-2 ${
    isDark ? "text-gray-300" : "text-gray-700"
  }`;

  const inputClass = `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
    isDark
      ? "bg-gray-700/30 border-gray-600 text-white"
      : "bg-white border-gray-300 text-gray-900"
  }`;

  const setFilter = (name, value) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  /* ------------------------- INIT LOAD ------------------------- */
  const loadInit = useCallback(async () => {
    try {
      setPageLoading(true);

      const { data } = await axios.get(`${API_BASE}/init`, {
        params: {
          branchId: toInt(branchId, 0),
          loginUserId: toInt(loginUserId, 0),
          branchType: branch,
        },
        headers: commonHeaders,
      });

      if (!data?.success) {
        throw new Error(data?.message || "Failed to load initial data");
      }

      setDd({
        batteryCodes: Array.isArray(data?.dropdowns?.batteryCodes)
          ? data.dropdowns.batteryCodes
          : defaultDropdowns.batteryCodes,
        batteryTypes: Array.isArray(data?.dropdowns?.batteryTypes)
          ? data.dropdowns.batteryTypes
          : defaultDropdowns.batteryTypes,
        stores: Array.isArray(data?.dropdowns?.stores)
          ? data.dropdowns.stores
          : defaultDropdowns.stores,
        vehicleTypes: Array.isArray(data?.dropdowns?.vehicleTypes)
          ? data.dropdowns.vehicleTypes
          : defaultDropdowns.vehicleTypes,
        batteryStatus: Array.isArray(data?.dropdowns?.batteryStatus)
          ? data.dropdowns.batteryStatus
          : defaultDropdowns.batteryStatus,
      });

      if (data?.defaults?.batteryStatus) {
        setFilters((prev) => ({
          ...prev,
          batteryStatus: data.defaults.batteryStatus,
        }));
      }
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to load dropdowns"
      );
    } finally {
      setPageLoading(false);
    }
  }, [branchId, loginUserId, branch, commonHeaders]);

  /* ------------------------- LIST LOAD ------------------------- */
  const fetchList = useCallback(
    async (
      nextPage = page,
      nextLimit = limit,
      nextFilters = filters,
      isSilent = false
    ) => {
      try {
        if (!isSilent) setLoading(true);

        const params = {
          branchId: toInt(branchId, 0),
          branchType: branch,
          page: nextPage,
          limit: nextLimit,
          vehicleName: toStr(nextFilters.vehicleName),
          vehicleRegNo: toStr(nextFilters.vehicleRegNo),
          batteryCodeId: toStr(nextFilters.batteryCodeId, "Select") || "Select",
          batteryTypeId: toStr(nextFilters.batteryTypeId, "Select") || "Select",
          storeId: toStr(nextFilters.storeId, "Select") || "Select",
          vehicleTypeIndex:
            toStr(nextFilters.vehicleTypeIndex, "Select") || "Select",
          batteryStatus:
            toStr(nextFilters.batteryStatus, "Process") || "Process",
          fittedDate: toStr(nextFilters.fittedDate),
        };

        const { data } = await axios.get(`${API_BASE}/list`, {
          params,
          headers: commonHeaders,
        });

        if (!data?.success) {
          throw new Error(data?.message || "Failed to load list");
        }

        const mapped = Array.isArray(data?.rows)
          ? data.rows.map(mapRow)
          : [];

        setRows(mapped);
        setTotal(toInt(data?.total, 0));
        setPage(toInt(data?.page, nextPage));
        setLimit(toInt(data?.limit, nextLimit));

        setSelectedIds([]);
        setSelectAll(false);
      } catch (err) {
        console.error(err);
        toast.error(
          err?.response?.data?.message ||
            err?.message ||
            "Failed to load list"
        );
        setRows([]);
        setTotal(0);
      } finally {
        if (!isSilent) setLoading(false);
      }
    },
    [page, limit, filters, branchId, branch, commonHeaders]
  );

  useEffect(() => {
    loadInit();
  }, [loadInit]);

  useEffect(() => {
    fetchList(1, limit, filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ------------------------- SELECTION ------------------------- */
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedIds([]);
      setSelectAll(false);
      return;
    }

    const selectableIds = rows
      .filter(
        (x) => String(x?.batterystatus || "").toLowerCase() !== "scrapped"
      )
      .map((x) => x.id)
      .filter((x) => x > 0);

    setSelectedIds(selectableIds);
    setSelectAll(true);
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  useEffect(() => {
    const selectableIds = rows
      .filter(
        (x) => String(x?.batterystatus || "").toLowerCase() !== "scrapped"
      )
      .map((x) => x.id)
      .filter((x) => x > 0);

    setSelectAll(
      selectableIds.length > 0 &&
        selectableIds.every((id) => selectedIds.includes(id))
    );
  }, [rows, selectedIds]);

  /* ------------------------- ACTIONS ------------------------- */
  const handleSearch = () => {
    setPage(1);
    fetchList(1, limit, filters);
  };

  const handleViewAll = () => {
    const resetFilters = { ...emptyFilters };
    setFilters(resetFilters);
    setPage(1);
    fetchList(1, limit, resetFilters);
  };

  const goEdit = (id) => {
    if (!id) return;
    navigate(`/vehicle/BatteryCardMaintenanceEntry?batteryCardMainId=${id}`);
  };

  const doDelete = useCallback(async () => {
    if (selectedIds.length === 0) {
      toast.warn("Please select at least one battery card to delete!");
      return;
    }

    try {
      setLoading(true);

      const { data } = await axios.post(
        `${API_BASE}/delete`,
        {
          ids: selectedIds,
          loginUserId: toInt(loginUserId, 0),
        },
        { headers: commonHeaders }
      );

      if (!data?.success) {
        throw new Error(data?.message || "Delete failed");
      }

      toast.success(data?.message || "Deleted Successfully");
      await fetchList(page, limit, filters, true);
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message || err?.message || "Delete failed"
      );
    } finally {
      setLoading(false);
    }
  }, [selectedIds, loginUserId, commonHeaders, fetchList, page, limit, filters]);

  const doScrap = useCallback(
    async (batteryCardMainId) => {
      try {
        setLoading(true);

        const { data } = await axios.post(
          `${API_BASE}/scrap`,
          { batteryCardMainId: toInt(batteryCardMainId, 0) },
          { headers: commonHeaders }
        );

        if (!data?.success) {
          throw new Error(data?.message || "Scrap failed");
        }

        toast.success(data?.message || "Battery card scrapped successfully");
        await fetchList(page, limit, filters, true);
      } catch (err) {
        console.error(err);
        toast.error(
          err?.response?.data?.message || err?.message || "Scrap failed"
        );
      } finally {
        setLoading(false);
      }
    },
    [commonHeaders, fetchList, page, limit, filters]
  );

  /* ------------------------- PAGINATION ------------------------- */
  const goPrev = () => {
    const p = Math.max(1, page - 1);
    setPage(p);
    fetchList(p, limit, filters);
  };

  const goNext = () => {
    const p = Math.min(totalPages, page + 1);
    setPage(p);
    fetchList(p, limit, filters);
  };

  const changeLimit = (e) => {
    const nextLimit = toInt(e.target.value, 10);
    setLimit(nextLimit);
    setPage(1);
    fetchList(1, nextLimit, filters);
  };

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={
            <span className="font-dmserif text-3xl">
              BATTERY CARD MAINTENANCE
            </span>
          }
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `bg-white ${currentSkin.glass} text-black`
          }`}
        >
          {/* Header */}
          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-6">
            <div className="flex items-center gap-4 flex-wrap">
              <h1
                className={`text-2xl font-bold ${
                  isDark ? "text-white" : "text-gray-900"
                }`}
              >
                Battery Card Maintenance Listing
              </h1>

              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  isDark
                    ? "bg-blue-900 text-blue-100"
                    : "bg-blue-100 text-blue-800"
                }`}
              >
                {total} Records
              </span>
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              <button
                onClick={() => setShowFilters((prev) => !prev)}
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800" +
                      (showFilters ? " bg-gray-800" : "")
                    : "border-gray-300 text-gray-700 hover:bg-gray-50" +
                      (showFilters ? " bg-gray-50" : "")
                }`}
                disabled={pageLoading || loading}
              >
                <FaFilter className="w-4 h-4" />
                {showFilters ? "Hide Filters" : "Show Filters"}
                {showFilters ? <FaChevronUp /> : <FaChevronDown />}
              </button>

              <button
                onClick={() => navigate("/vehicle/BatteryCardMaintenanceEntry")}
                className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-4 py-2 rounded transition-colors`}
              >
                <FaPlus className="w-4 h-4" />
                Add New
              </button>
            </div>
          </div>

          {/* Filters */}
          {showFilters && (
            <div
              className={`rounded-xl p-6 mb-6 border ${
                isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
              }`}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <FaFilter /> FILTRATION AREA
                </h3>
              </div>

              {pageLoading ? (
                <div className="text-center py-8">Loading...</div>
              ) : (
                <>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                    {/* LEFT */}
                    <div className="space-y-5">
                      <div className="grid grid-cols-12 gap-3 items-center">
                        <label className={`${labelClass} col-span-4 mb-0`}>
                          Store
                        </label>
                        <div className="col-span-8">
                          <select
                            className={inputClass}
                            value={filters.storeId}
                            onChange={(e) => setFilter("storeId", e.target.value)}
                            disabled={loading || pageLoading}
                          >
                            {(dd.stores || []).map((o, i) => (
                              <option key={i} value={o.value}>
                                {o.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className="grid grid-cols-12 gap-3 items-center">
                        <label className={`${labelClass} col-span-4 mb-0`}>
                          Vehicle Name
                        </label>
                        <div className="col-span-8">
                          <input
                            type="text"
                            value={filters.vehicleName}
                            onChange={(e) =>
                              setFilter("vehicleName", e.target.value)
                            }
                            className={inputClass}
                            disabled={loading || pageLoading}
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-12 gap-3 items-center">
                        <label className={`${labelClass} col-span-4 mb-0`}>
                          Battery Code
                        </label>
                        <div className="col-span-8">
                          <select
                            className={inputClass}
                            value={filters.batteryCodeId}
                            onChange={(e) =>
                              setFilter("batteryCodeId", e.target.value)
                            }
                            disabled={loading || pageLoading}
                          >
                            {(dd.batteryCodes || []).map((o, i) => (
                              <option key={i} value={o.value}>
                                {o.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className="grid grid-cols-12 gap-3 items-center">
                        <label className={`${labelClass} col-span-4 mb-0`}>
                          Vehicle Main Type
                        </label>
                        <div className="col-span-8">
                          <select
                            className={inputClass}
                            value={filters.vehicleTypeIndex}
                            onChange={(e) =>
                              setFilter("vehicleTypeIndex", e.target.value)
                            }
                            disabled={loading || pageLoading}
                          >
                            {(dd.vehicleTypes || []).map((o, i) => (
                              <option key={i} value={o.value}>
                                {o.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>

                    {/* RIGHT */}
                    <div className="space-y-5">
                      <div className="grid grid-cols-12 gap-3 items-center">
                        <label className={`${labelClass} col-span-4 mb-0`}>
                          Vehicle Number
                        </label>
                        <div className="col-span-8">
                          <input
                            type="text"
                            value={filters.vehicleRegNo}
                            onChange={(e) =>
                              setFilter("vehicleRegNo", e.target.value)
                            }
                            className={inputClass}
                            disabled={loading || pageLoading}
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-12 gap-3 items-center">
                        <label className={`${labelClass} col-span-4 mb-0`}>
                          Battery Type
                        </label>
                        <div className="col-span-8">
                          <select
                            className={inputClass}
                            value={filters.batteryTypeId}
                            onChange={(e) =>
                              setFilter("batteryTypeId", e.target.value)
                            }
                            disabled={loading || pageLoading}
                          >
                            {(dd.batteryTypes || []).map((o, i) => (
                              <option key={i} value={o.value}>
                                {o.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className="grid grid-cols-12 gap-3 items-center">
                        <label className={`${labelClass} col-span-4 mb-0`}>
                          Fitted Date
                        </label>
                        <div className="col-span-8">
                          <input
                            type="text"
                            value={filters.fittedDate}
                            onChange={(e) =>
                              setFilter("fittedDate", e.target.value)
                            }
                            placeholder="dd/MM/yyyy"
                            className={inputClass}
                            disabled={loading || pageLoading}
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-12 gap-3 items-center">
                        <label className={`${labelClass} col-span-4 mb-0`}>
                          Battery Status
                        </label>
                        <div className="col-span-8">
                          <select
                            className={inputClass}
                            value={filters.batteryStatus}
                            onChange={(e) =>
                              setFilter("batteryStatus", e.target.value)
                            }
                            disabled={loading || pageLoading}
                          >
                            {(dd.batteryStatus || []).map((o, i) => (
                              <option key={i} value={o.value}>
                                {o.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-center gap-4 mt-8">
                    <button
                      onClick={handleSearch}
                      disabled={loading || pageLoading}
                      className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-8 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-200 ${
                        loading || pageLoading ? "opacity-70 cursor-not-allowed" : ""
                      }`}
                    >
                      <FaSearch />
                      Search
                    </button>

                    <button
                      onClick={handleViewAll}
                      disabled={loading || pageLoading}
                      className={`flex items-center gap-2 px-8 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-200 ${
                        isDark
                          ? "bg-gray-700 hover:bg-gray-600 text-white"
                          : "bg-gray-200 hover:bg-gray-300 text-gray-900"
                      } ${loading || pageLoading ? "opacity-70 cursor-not-allowed" : ""}`}
                    >
                      View All
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Table */}
          <div
            className={`rounded-xl shadow-sm border overflow-hidden ${
              isDark ? "border-gray-700" : "border-gray-200"
            }`}
          >
            {pageLoading || loading ? (
              <div className="text-center py-16">Loading...</div>
            ) : rows.length === 0 ? (
              <div className="text-center py-16">
                <div
                  className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-6 ${
                    isDark ? "bg-gray-700" : "bg-gray-100"
                  }`}
                >
                  <FaBatteryFull className="w-10 h-10 text-gray-400" />
                </div>
                <h3
                  className={`text-xl font-semibold mb-2 ${
                    isDark ? "text-gray-200" : "text-gray-800"
                  }`}
                >
                  No Battery Cards Found
                </h3>
                <p className={`${isDark ? "text-gray-400" : "text-gray-500"}`}>
                  Try adjusting your search.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className={`${buttonClass} text-white`}>
                    <tr>
                      <th className="px-4 py-4 text-center font-semibold">S.No</th>
                      <th className="px-4 py-4 text-center font-semibold">Store</th>
                      <th className="px-4 py-4 text-center font-semibold">
                        Vehicle Main Type
                      </th>
                      <th className="px-4 py-4 text-center font-semibold">
                        Vehicle Code
                      </th>
                      <th className="px-4 py-4 text-center font-semibold">
                        Vehicle Name
                      </th>
                      <th className="px-4 py-4 text-center font-semibold">
                        Vehicle RegistrationNumber
                      </th>
                      <th className="px-4 py-4 text-center font-semibold">
                        Battery Code
                      </th>
                      <th className="px-4 py-4 text-center font-semibold">
                        Battery Type
                      </th>
                      <th className="px-4 py-4 text-center font-semibold">
                        Fitted Date
                      </th>
                      <th className="px-4 py-4 text-center font-semibold">
                        current/Remove date
                      </th>
                      <th className="px-4 py-4 text-center font-semibold">
                        Actual Life
                      </th>
                      <th className="px-4 py-4 text-center font-semibold">
                        Current Life
                      </th>
                      <th className="px-4 py-4 text-center font-semibold">Alert</th>
                      <th className="px-4 py-4 text-center font-semibold">Edit</th>
                      <th className="px-4 py-4 text-center font-semibold">Testing</th>
                      <th className="px-4 py-4 text-center font-semibold">Scrap</th>
                      <th className="px-4 py-4 text-center font-semibold">
                        <div className="flex items-center justify-center">
                          Select
                          <input
                            type="checkbox"
                            checked={selectAll}
                            onChange={handleSelectAll}
                            className="ml-2"
                          />
                        </div>
                      </th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {rows.map((card, idx) => {
                      const disabled =
                        String(card.batterystatus || "").toLowerCase() === "scrapped";

                      return (
                        <tr
                          key={card.id || idx}
                          className={`transition-colors duration-200 ${
                            disabled
                              ? "opacity-60"
                              : isDark
                              ? "bg-gray-900"
                              : "bg-white"
                          }`}
                        >
                          <td
                            className={`px-4 py-4 whitespace-nowrap text-sm text-center ${
                              isDark ? "text-gray-300" : "text-gray-900"
                            }`}
                          >
                            {(page - 1) * limit + idx + 1}
                          </td>

                          {[
                            card.store,
                            card.vehicleMainType,
                            card.vehicleCode,
                            card.vehicleName,
                            card.vehicleRegistrationNumber,
                            card.batteryCode,
                            card.batteryType,
                            card.fittedDate,
                            card.currentRemoveDate,
                            card.actualLife,
                            card.currentLife,
                          ].map((val, i) => (
                            <td
                              key={i}
                              className={`px-4 py-4 whitespace-nowrap text-sm text-center ${
                                isDark ? "text-gray-300" : "text-gray-900"
                              }`}
                            >
                              {val}
                            </td>
                          ))}

                          <td className="px-4 py-3 text-sm text-center">
                            <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs">
                              {card.alert || "-"}
                            </span>
                          </td>

                          <td className="px-4 py-3 text-sm text-center">
                            <button
                              onClick={() => goEdit(card.id)}
                              className="inline-flex items-center gap-2 px-2 py-1 bg-green-100 text-green-800 rounded text-xs hover:bg-green-200"
                              disabled={disabled}
                              title={
                                disabled
                                  ? "Scrapped item cannot be edited"
                                  : "Edit"
                              }
                            >
                              <FaEdit />
                              Edit
                            </button>
                          </td>

                          <td className="px-4 py-3 text-sm text-center">
                            <button
                              onClick={() =>
                                navigate("/vehicle/BatteryTesting", {
                                  state: { batteryCardMainId: card.id },
                                })
                              }
                              className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs hover:bg-blue-200"
                              disabled={disabled}
                            >
                              Testing
                            </button>
                          </td>

                          <td className="px-4 py-3 text-sm text-center">
                            <button
                              onClick={() => doScrap(card.id)}
                              className="px-2 py-1 bg-red-100 text-red-800 rounded text-xs hover:bg-red-200"
                              disabled={disabled}
                              title={disabled ? "Already scrapped" : "Scrap"}
                            >
                              Scrap
                            </button>
                          </td>

                          <td className="px-4 py-3 text-center">
                            <input
                              type="checkbox"
                              checked={selectedIds.includes(card.id)}
                              onChange={() => toggleSelect(card.id)}
                              disabled={disabled}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Pagination + Delete */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mt-6">
            <div className="flex items-center gap-3 flex-wrap">
              <button
                onClick={goPrev}
                className={`px-3 py-2 rounded border ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                }`}
                disabled={page <= 1 || loading || pageLoading}
              >
                Prev
              </button>

              <div className={`${isDark ? "text-gray-200" : "text-gray-800"} text-sm`}>
                Page <b>{page}</b> / <b>{totalPages}</b>
              </div>

              <button
                onClick={goNext}
                className={`px-3 py-2 rounded border ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                }`}
                disabled={page >= totalPages || loading || pageLoading}
              >
                Next
              </button>

              <select
                value={limit}
                onChange={changeLimit}
                disabled={loading || pageLoading}
                className={`px-2 py-2 rounded border ${
                  isDark
                    ? "bg-gray-800 border-gray-600 text-white"
                    : "bg-white border-gray-300 text-gray-800"
                }`}
              >
                {[10, 20, 50, 100].map((x) => (
                  <option key={x} value={x}>
                    {x} / page
                  </option>
                ))}
              </select>
            </div>

            <div className="flex justify-end">
              <button
                onClick={doDelete}
                className={`flex items-center gap-2 px-4 py-2 text-white text-sm rounded-lg shadow transition ${
                  selectedIds.length === 0 || loading || pageLoading
                    ? "bg-gray-400 cursor-not-allowed"
                    : `${buttonClass} ${hoverClass}`
                }`}
                disabled={selectedIds.length === 0 || loading || pageLoading}
              >
                <FaTrash /> Delete Selected
              </button>
            </div>
          </div>

          <div className={`mt-8 pt-6 border-t ${isDark ? "border-gray-700" : "border-gray-200"}`}>
            <p className={`text-sm text-center ${isDark ? "text-gray-400" : "text-gray-500"}`}>
              INFRA SCREEN : BATTERY CARD MAINTENANCE LISTING
            </p>
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default BatteryCardMaintenanceListing;